import { csrfHeaders, forgetCsrfToken, toApiError } from '@/lib/http';
import {
  parseQueueView,
  type QueueSort,
  type QueueTab,
  type QueueView,
  type RejectCause,
} from './reviewTypes';

/**
 * The reviewer's requests.
 *
 * The read takes the `AbortSignal` through to `fetch`, so switching tab
 * cancels the outgoing request rather than letting a slower answer for the
 * previous tab overwrite the current one.
 *
 * The writes do not. Approving and rejecting are not idempotent from this
 * side of the wire, and aborting one mid-flight tells us nothing about
 * whether the server completed it.
 */

export async function fetchQueue(
  tab: QueueTab,
  language: string | null,
  overdueOnly: boolean,
  sort: QueueSort,
  signal: AbortSignal,
): Promise<QueueView> {
  const params = new URLSearchParams({ tab, sort });
  if (language !== null) {
    params.set('language', language);
  }
  if (overdueOnly) {
    params.set('overdueOnly', 'true');
  }

  const response = await fetch(`/api/editor/review/queue?${params}`, {
    signal,
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw await toApiError(response);
  }
  return parseQueueView(await response.json());
}

async function write(url: string, body: unknown): Promise<unknown> {
  const csrf = await csrfHeaders(new AbortController().signal);
  const response = await fetch(url, {
    method: 'POST',
    headers: { ...csrf, Accept: 'application/json', 'Content-Type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      forgetCsrfToken();
    }
    throw await toApiError(response);
  }
  return response.json();
}

/** A row's id is `<khutbahId>:<language>` — one language of one khutbah. */
export function splitRowId(rowId: string): { khutbahId: string; language: string } {
  const separator = rowId.lastIndexOf(':');
  return separator === -1
    ? { khutbahId: rowId, language: '' }
    : {
        khutbahId: rowId.slice(0, separator),
        language: rowId.slice(separator + 1),
      };
}

export async function approveRow(rowId: string): Promise<void> {
  const { khutbahId, language } = splitRowId(rowId);
  await write(
    `/api/editor/khutbah/${encodeURIComponent(khutbahId)}/approve?language=${encodeURIComponent(language)}`,
    undefined,
  );
}

/**
 * @param keepReadable leave it in front of readers while flagged. The
 *                     exception, never the default — rejecting means the
 *                     reviewer decided it should not be read.
 */
export async function rejectRow(
  rowId: string,
  causes: readonly RejectCause[],
  note: string,
  keepReadable: boolean,
): Promise<void> {
  const { khutbahId, language } = splitRowId(rowId);
  await write(
    `/api/editor/khutbah/${encodeURIComponent(khutbahId)}/reject?language=${encodeURIComponent(language)}`,
    { causes, note, keepReadable },
  );
}
